import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Wand2, Loader2 } from "lucide-react";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Per-pair distance editor.
 * value shape: { [pickup: string]: { [dropoff: string]: number } }
 * onChange receives the updated matrix.
 *
 * Iter 67da — vendors can now hit "Auto-fill from catalog" to compute
 * great-circle km between every pickup/drop-off pair using the
 * coordinates stored in the locations catalog. Anything the catalog
 * can't resolve (custom one-off locations, typos) is reported back
 * as "unresolved" and left untouched so the vendor can still type a
 * manual override.
 */
export default function DistanceMatrix({ pickups = [], dropoffs = [], value = {}, onChange }) {
  const [autoLoading, setAutoLoading] = useState(false);

  const autoFill = async () => {
    if (pickups.length === 0 || dropoffs.length === 0) return;
    setAutoLoading(true);
    try {
      const { data } = await api.post("/locations/distances", {
        pickups, dropoffs,
      });
      const next = { ...(value || {}) };
      let filled = 0;
      for (const [p, row] of Object.entries(data?.matrix || {})) {
        next[p] = { ...(next[p] || {}) };
        for (const [d, km] of Object.entries(row)) {
          if (km === null || km === undefined) continue;
          if (p === d) continue;
          next[p][d] = km;
          filled += 1;
        }
      }
      onChange(next);
      const unresolved = data?.unresolved || [];
      const usedGoogle = data?.source === "google_with_haversine_fallback";
      // Iter 67db — source disclosure: Google driving km when the API key
      // is set, straight-line haversine otherwise. The toast spells out
      // which one was used so vendors know how accurate the value is.
      const sourceTag = usedGoogle ? "Google Maps driving km" : "straight-line (approx)";
      if (unresolved.length > 0) {
        toast.warning(`Filled ${filled} cells (${sourceTag}). Missing coords: ${unresolved.join(", ")}`);
      } else {
        toast.success(`Auto-filled ${filled} distance${filled === 1 ? "" : "s"} · ${sourceTag}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAutoLoading(false);
    }
  };

  if (pickups.length === 0 || dropoffs.length === 0) {
    return (
      <div className="rounded-xl bg-neutral-50 border border-dashed border-border p-4 text-sm text-neutral-500" data-testid="matrix-empty">
        Add at least one pickup and one drop-off destination to set distances.
      </div>
    );
  }

  const setCell = (p, d) => (e) => {
    const raw = e.target.value;
    const next = { ...(value || {}) };
    next[p] = { ...(next[p] || {}) };
    if (raw === "" || raw === null) {
      delete next[p][d];
    } else {
      const v = parseFloat(raw);
      next[p][d] = isNaN(v) ? 0 : Math.max(0, v);
    }
    onChange(next);
  };

  const cellValue = (p, d) => {
    if (p === d) return 0;
    const v = value?.[p]?.[d];
    return v === undefined || v === null ? "" : v;
  };

  return (
    <div className="space-y-2" data-testid="distance-matrix">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Distance matrix (km)</div>
          <p className="text-xs text-neutral-500 mt-1">Same-location pairs are auto-zero. Leave a cell blank if you don't want to charge for that route.</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={autoFill}
          disabled={autoLoading}
          className="rounded-xl shrink-0"
          data-testid="matrix-auto-fill"
        >
          {autoLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
          Auto-fill from catalog
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-neutral-500 font-medium">Pickup ↓ / Drop-off →</th>
              {dropoffs.map((d) => (
                <th key={d} className="px-3 py-2 text-left text-xs uppercase tracking-wider text-neutral-500 font-medium">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pickups.map((p) => (
              <tr key={p} className="border-t border-border">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{p}</td>
                {dropoffs.map((d) => (
                  <td key={d} className="px-2 py-1.5">
                    {p === d ? (
                      <Input
                        value="0"
                        disabled
                        className="rounded-lg h-9 w-24 bg-neutral-100"
                        data-testid={`matrix-cell-${p}-${d}`}
                      />
                    ) : (
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="km"
                        value={cellValue(p, d)}
                        onChange={setCell(p, d)}
                        className="rounded-lg h-9 w-24"
                        data-testid={`matrix-cell-${p}-${d}`}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
